import { pageUrls } from 'globalconfig/constants/keys';
import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from 'globalconfig/constants/static';
import { hasValue, objectArrayFromDiffFields, objectArrayFromSameField } from 'globalutils/fmUtils';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { lazy, useState } from 'react';
import { getSchema } from './schema';
import { checkServiceProvider } from './serviceProviderConfig';

const layout = window.$environment?.CURRENT_LAYOUT;
const Form = lazy(() => import(`globalcomponents/UI/Form/${layout}`));
const Timezone = lazy(() => import(`@/modules/Timezone/${layout}`));
const Modal = require(`globalcomponents/UI/Modal/${layout}`).default;
const ErrorIcon = lazy(() => import(`globalcomponents/ErrorIcon/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));

const CompanyDataForm = ({
  apiStatus,
  history,
  Styled,
  i18n,
  innerComponent,
  companyData,
  states,
  saveCompanyData,
  callback,
  cancelSubscription,
  allCycleRules,
  allCycleExceptions,
  allVehicleTypes,
  updateCardDetails,
  updateCard,
  insurancePortalUser,
  type,
}) => {
  const [AddEditModalObject, setAddEditModalObject] = useState({
    header: '',
    content: () => {
      return <> </>;
    },
  });
  const [addOpen, setAddOpen] = useState(false);

  const clickedAddEditModal = () => {
    setAddOpen(true);
  };

  const closeAddEditModal = () => {
    setAddOpen(false);
  };

  const apiId = type + 'SaveFleetCompany';
  const hasSaveApiStatus = hasValue(apiStatus.status, 'api', apiId);
  const RenderComponent = lazy(() => import(`@/${innerComponent}`));
  const radiusCompliances = objectArrayFromSameField(companyData?.radiusCompliances);
  const underWriters = objectArrayFromDiffFields(companyData?.underwriteres, 'id', 'fullName');
  const lossControllers = objectArrayFromDiffFields(companyData?.lossControles, 'id', 'fullName');

  const getTmezonePopup = (type, message, payload) => {
    payload = {
      ...payload,
      data: {
        ...payload.data,
        timeZone: companyData?.companyDto?.timeZoneId,
      },
    };

    if (type === 'error') {
      setAddEditModalObject({
        header: '',
        content: () => {
          return (
            <div className="successpopup">
              <ErrorIcon />
              <h2> {i18n.t(message)} </h2>
              <div className="deletePopupBtn">
                <Button
                  type="submit"
                  label={i18n.t('common.ok')}
                  onClick={() => {
                    closeAddEditModal();
                  }}
                />
              </div>
            </div>
          );
        },
      });
      clickedAddEditModal();
    }
    if (message === 'Time zone not available') {
      setAddEditModalObject({
        header: '',
        content: () => {
          return (
            <Timezone
              closeModal={closeAddEditModal}
              callback={callback}
              payload={payload}
              saveCompanyData={saveCompanyData}
              successCallback={() => {
                history.push(pageUrls.fleetCompanies);
              }}
            />
          );
        },
      });
      clickedAddEditModal();
    }
  };

  const getData = (fields) => {
    const formattedFields = {
      ...fields,
      state: fields.state?.value,
      radiusCompliance: fields.radiusCompliance?.value,
      lossControlId: fields.lossControlId?.value,
      underwriterId: fields.underwriterId?.value,
      insuranceExpDate: moment(fields.insuranceExpDate).format(BACKEND_DATE_FORMAT),
      freeTrialEndDate: fields.freeTrialEndDate ? moment(fields.freeTrialEndDate).format(BACKEND_DATE_FORMAT) : null,
    };

    if (type !== 'edit') {
      formattedFields.initialTrucks = fields?.initialTrucks || null;
    } else {
      delete formattedFields['initialTrucks'];
    }

    if (type === 'edit') {
      Object.assign(formattedFields, {
        maintenancePeriod: fields?.maintenancePeriod?.value,
        assignedVehicleType: fields?.assignedVehicleType?.value || null,
        defaultCycleRule: fields?.defaultCycleRule?.value || null,
        assignedCycleExceptions: fields?.assignedCycleExceptions
          ? fields.assignedCycleExceptions.map((key) => {
              return key.value;
            })
          : null,
        assignedCycleRules: fields?.assignedCycleRules
          ? fields.assignedCycleRules.map((key) => {
              return key.value;
            })
          : null,
        ...checkServiceProvider('form_getData_edit', window?.$environment?.SERVICE_PROVIDER, false, { fields }),
      });
    }

    ['activeTrucks', 'zohoId', 'timeZone', 'contractExpirationDate', 'currentSubscriptionCost'].forEach(
      (key) => delete formattedFields[key]
    );

    return formattedFields;
  };

  const handleSubmit = (fields) => {
    saveCompanyData({
      apiId,
      data: getData(fields),
      type,
      callback: getTmezonePopup,
      successCallback: () => {
        history.push(pageUrls.fleetCompanies);
      },
    });
  };

  return (
    <>
      <Form
        type="server"
        direction="column"
        initialValues={{
          autoPaymentEnabled: (type === 'add' ? true : companyData?.companyDto?.autoPaymentEnabled) || false,
          freeTrialEndDate: companyData?.companyDto?.freeTrialEndDate
            ? moment(companyData?.companyDto?.freeTrialEndDate).format(UI_DATE_FORMAT)
            : '',
          companyId: companyData?.companyDto?.companyId || '',
          company: companyData?.companyDto?.company || '',
          dotNumber: companyData?.companyDto?.dotNumber || '',
          address: companyData?.companyDto?.address || '',
          city: companyData?.companyDto?.city || '',
          speedLimit: companyData?.companyDto?.speedLimit || '',
          zohoId: companyData?.companyDto?.zohoCustomerId || '',
          state: companyData?.companyDto?.state
            ? {
                label: companyData?.companyDto?.state,
                value: companyData?.companyDto?.state,
              }
            : '',
          insuranceExpDate: companyData?.companyDto?.insuranceExpDate
            ? moment(companyData?.companyDto?.insuranceExpDate).format(UI_DATE_FORMAT)
            : '',
          radiusCompliance: companyData?.companyDto?.radiusCompliance
            ? {
                label: companyData?.companyDto?.radiusCompliance,
                value: companyData?.companyDto?.radiusCompliance,
              }
            : '',
          underwriterId: underWriters.find((uw) => uw.value === companyData?.companyDto?.underwriterId) || '',
          lossControlId: lossControllers.find((lc) => lc.value === companyData?.companyDto?.lossControlId) || '',
          radiusTolerance: companyData?.companyDto?.radiusTolerance || '',
          ...(type === 'edit'
            ? {
                subscriptionTier: companyData?.companyDto?.subscriptionTier || '',
                maintenancePeriod:
                  companyData?.companyDto?.maintenancePeriod >= 0
                    ? {
                        label: companyData?.companyDto?.maintenancePeriod,
                        value: companyData?.companyDto?.maintenancePeriod,
                      }
                    : '',
                maintenanceMileage: companyData?.companyDto?.maintenanceMileage || 0,
                zipCode: companyData?.companyDto?.zipCode || '',
                companyPhone: companyData?.companyDto?.companyPhone || '',
                initialTrucks: companyData?.companyDto?.initialTrucks || 0,
                activeTrucks: companyData?.companyDto?.activeTrucks || 0,
                timeZone: companyData?.companyDto?.timeZone || '',
                contactPerson: companyData?.companyDto?.contactPerson || '',
                otherLocationContactPerson: companyData?.companyDto?.otherLocationContactPerson || '',
                otherLocationAddress: companyData?.companyDto?.otherLocationAddress || '',
                otherLocationPhone: companyData?.companyDto?.otherLocationPhone || '',
                website: companyData?.companyDto?.website || '',
                skipVehicleFilter: companyData?.companyDto?.skipVehicleFilter || false,
                assignedVehicleType:
                  companyData?.assignedVehicleType !== null
                    ? {
                        label: companyData?.assignedVehicleType,
                        value: companyData?.assignedVehicleType,
                      }
                    : allVehicleTypes[0],
                assignedCycleRules:
                  companyData?.assignedCycleRules !== null
                    ? companyData?.assignedCycleRules && objectArrayFromSameField(companyData?.assignedCycleRules)
                    : '',
                assignedCycleExceptions:
                  companyData?.assignedCycleExceptions !== null
                    ? companyData?.assignedCycleExceptions && objectArrayFromSameField(companyData?.assignedCycleExceptions)
                    : '',
                defaultCycleRule: companyData?.defaultCycleRule
                  ? {
                      label: companyData?.defaultCycleRule,
                      value: companyData?.defaultCycleRule,
                    }
                  : '',
                showOw: companyData?.companyDto?.showOw || false,
                contractExpirationDate: moment(companyData?.companyDto?.contractExpirationDate).format('L'),
                currentSubscriptionCost: companyData?.companyDto?.currentSubscriptionCost
                  ? `$${(companyData?.companyDto?.currentSubscriptionCost / 100).toFixed(2)}`
                  : '$0.00',
                isIftaEnabled: companyData?.companyDto?.isIftaEnabled,
                isDvirEnabled: companyData?.companyDto?.isDvirEnabled,
                clutchSubscriptionPlan: companyData?.companyDto?.clutchSubscriptionPlan,
              }
            : {
                firstName: companyData?.companyDto?.firstName || '',
                lastName: companyData?.companyDto?.lastName || '',
                email: companyData?.companyDto?.email || '',
                userPhone: companyData?.companyDto?.userPhone || '',
                password: companyData?.companyDto?.password || '',
              }),
        }}
        RenderComponent={RenderComponent}
        handleOnSubmit={handleSubmit}
        customProps={{
          hasSaveApiStatus,
          history,
          Styled,
          i18n,
          states,
          cancelSubscription,
          apiStatus,
          callback,
          allCycleRules,
          allCycleExceptions,
          allVehicleTypes,
          updateCard,
          updateCardDetails,
          insurancePortalUser,
          radiusCompliances,
          underWriters,
          lossControllers,
          type,
        }}
        handleOnCancel={() => {
          history.push(pageUrls.fleetCompanies);
        }}
        validationSchema={getSchema({ type })}
      />
      <Modal open={addOpen} setOpen={setAddOpen} data={AddEditModalObject} background="black" timeZone isSuccess />
    </>
  );
};
export default CompanyDataForm;

CompanyDataForm.defaultType = {
  hasRegistration: true,
};
CompanyDataForm.propTypes = {
  apiStatus: PropTypes.object.isRequired,
  fetchLogin: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  Styled: PropTypes.object.isRequired,
  i18n: PropTypes.array.isRequired,
  hasRegistration: PropTypes.bool,
};
